/**
 * Performance Monitor for Core Web Vitals
 * Tracks LCP, INP, CLS, FCP, and TTFB
 * Helps identify performance bottlenecks and INP issues
 */

class PerformanceMonitor {
  constructor() {
    this.metrics = {};
    this.observers = [];
    this.debug = window.location.search.includes('debug=performance');
    
    this.init();
  }

  init() {
    // Only run in supported browsers
    if (!('PerformanceObserver' in window)) {
      console.warn('PerformanceObserver not supported');
      return;
    }

    this.observeLCP();
    this.observeINP();
    this.observeCLS();
    this.observeFCP();
    this.observeTTFB();
    this.observeLongTasks();
    
    // Send metrics on page unload
    this.setupBeaconSending();
    
    if (this.debug) {
      this.setupDebugMode();
    }
  }

  /**
   * Observe Largest Contentful Paint (LCP)
   * Critical for understanding loading performance
   */
  observeLCP() {
    try {
      const observer = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        const lastEntry = entries[entries.length - 1];
        
        this.metrics.lcp = {
          value: lastEntry.startTime,
          element: lastEntry.element?.tagName || 'unknown',
          url: lastEntry.url || '',
          timestamp: Date.now()
        };
        
        this.logMetric('LCP', this.metrics.lcp.value, 'ms');
        
        // Flag slow LCP
        if (this.metrics.lcp.value > 2500) {
          console.warn(`Slow LCP detected: ${this.metrics.lcp.value}ms on ${this.metrics.lcp.element}`);
        }
      });
      
      observer.observe({ type: 'largest-contentful-paint', buffered: true });
      this.observers.push(observer);
    } catch (error) {
      console.error('LCP observation failed:', error);
    }
  }

  /**
   * Observe Interaction to Next Paint (INP)
   * Critical for understanding responsiveness issues
   */
  observeINP() {
    try {
      // Track all interactions
      let interactions = [];
      
      const observer = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          // Only track user interactions
          if (entry.name === 'click' || entry.name === 'keydown' || entry.name === 'pointerdown') {
            const interaction = {
              type: entry.name,
              startTime: entry.startTime,
              processingStart: entry.processingStart,
              processingEnd: entry.processingEnd,
              duration: entry.duration,
              target: entry.target?.tagName || 'unknown'
            };
            
            interactions.push(interaction);
            
            // Calculate INP (worst case scenario)
            const inp = Math.max(...interactions.map(i => i.duration));
            this.metrics.inp = {
              value: inp,
              worstInteraction: interactions.find(i => i.duration === inp),
              totalInteractions: interactions.length,
              timestamp: Date.now()
            };
            
            this.logMetric('INP', inp, 'ms');
            
            // Flag slow interactions
            if (inp > 200) {
              console.warn(`Slow interaction detected: ${inp}ms on ${interaction.target}`);
              this.debugSlowInteraction(interaction);
            }
          }
        }
      });
      
      observer.observe({ type: 'event', buffered: true });
      this.observers.push(observer);
      
      // Fallback for older browsers - manual interaction tracking
      this.setupManualINPTracking();
      
    } catch (error) {
      console.error('INP observation failed:', error);
      this.setupManualINPTracking();
    }
  }

  /**
   * Manual INP tracking for browsers without event timing API
   */
  setupManualINPTracking() {
    ['click', 'keydown', 'pointerdown'].forEach(eventType => {
      document.addEventListener(eventType, (event) => {
        const startTime = performance.now();
        
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            const endTime = performance.now();
            const duration = endTime - startTime;
            
            if (!this.metrics.inp || duration > this.metrics.inp.value) {
              this.metrics.inp = {
                value: duration,
                type: eventType,
                target: event.target?.tagName || 'unknown',
                timestamp: Date.now()
              };
              
              if (duration > 200) {
                console.warn(`Manual INP: Slow ${eventType} detected: ${duration}ms`);
              }
            }
          });
        });
      }, { passive: true });
    });
  }

  /**
   * Observe Cumulative Layout Shift (CLS)
   */
  observeCLS() {
    try {
      let clsValue = 0;
      
      const observer = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          if (!entry.hadRecentInput) {
            clsValue += entry.value;
          }
        }
        
        this.metrics.cls = {
          value: clsValue,
          timestamp: Date.now()
        };
        
        this.logMetric('CLS', clsValue.toFixed(4), '');
        
        if (clsValue > 0.1) {
          console.warn(`High CLS detected: ${clsValue.toFixed(4)}`);
        }
      });
      
      observer.observe({ type: 'layout-shift', buffered: true });
      this.observers.push(observer);
    } catch (error) {
      console.error('CLS observation failed:', error);
    }
  }

  /**
   * Observe First Contentful Paint (FCP)
   */
  observeFCP() {
    try {
      const observer = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          if (entry.name === 'first-contentful-paint') {
            this.metrics.fcp = {
              value: entry.startTime,
              timestamp: Date.now()
            };
            
            this.logMetric('FCP', entry.startTime, 'ms');
          }
        }
      });
      
      observer.observe({ type: 'paint', buffered: true });
      this.observers.push(observer);
    } catch (error) {
      console.error('FCP observation failed:', error);
    }
  }

  /**
   * Observe Time to First Byte (TTFB)
   */
  observeTTFB() {
    try {
      const navigation = performance.getEntriesByType('navigation')[0];
      if (navigation) {
        const ttfb = navigation.responseStart - navigation.requestStart;
        
        this.metrics.ttfb = {
          value: ttfb,
          timestamp: Date.now()
        };
        
        this.logMetric('TTFB', ttfb, 'ms');
      }
    } catch (error) {
      console.error('TTFB measurement failed:', error);
    }
  }

  /**
   * Observe Long Tasks (blocks main thread)
   */
  observeLongTasks() {
    try {
      const observer = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          if (entry.duration > 50) {
            console.warn(`Long task detected: ${entry.duration}ms at ${entry.startTime}ms`);
            
            if (!this.metrics.longTasks) {
              this.metrics.longTasks = [];
            }
            
            this.metrics.longTasks.push({
              duration: entry.duration,
              startTime: entry.startTime,
              timestamp: Date.now()
            });
          }
        }
      });
      
      observer.observe({ type: 'longtask', buffered: true });
      this.observers.push(observer);
    } catch (error) {
      console.error('Long task observation failed:', error);
    }
  }

  /**
   * Debug slow interactions to help fix INP issues
   */
  debugSlowInteraction(interaction) {
    if (!this.debug) return;
    
    console.group('🐌 Slow Interaction Debug');
    console.log('Type:', interaction.type);
    console.log('Target:', interaction.target);
    console.log('Duration:', interaction.duration + 'ms');
    console.log('Processing Time:', (interaction.processingEnd - interaction.processingStart) + 'ms');
    console.log('Input Delay:', (interaction.processingStart - interaction.startTime) + 'ms');
    console.log('Presentation Delay:', (interaction.startTime + interaction.duration - interaction.processingEnd) + 'ms');
    console.groupEnd();
  }

  /**
   * Log metrics to console with emoji indicators
   */
  logMetric(name, value, unit) {
    if (!this.debug) return;
    
    let emoji = '✅';
    let color = 'color: green';
    
    // Set thresholds and emojis based on metric
    switch (name) {
      case 'LCP':
        if (value > 4000) { emoji = '🔴'; color = 'color: red'; }
        else if (value > 2500) { emoji = '🟡'; color = 'color: orange'; }
        break;
      case 'INP':
        if (value > 500) { emoji = '🔴'; color = 'color: red'; }
        else if (value > 200) { emoji = '🟡'; color = 'color: orange'; }
        break;
      case 'CLS':
        if (value > 0.25) { emoji = '🔴'; color = 'color: red'; }
        else if (value > 0.1) { emoji = '🟡'; color = 'color: orange'; }
        break;
      case 'FCP':
        if (value > 3000) { emoji = '🔴'; color = 'color: red'; }
        else if (value > 1800) { emoji = '🟡'; color = 'color: orange'; }
        break;
    }
    
    console.log(`%c${emoji} ${name}: ${value}${unit}`, color);
  }

  /**
   * Setup debug mode with performance panel
   */
  setupDebugMode() {
    // Create floating debug panel
    const panel = document.createElement('div');
    panel.id = 'performance-debug-panel';
    panel.style.cssText = `
      position: fixed;
      top: 10px;
      right: 10px;
      background: rgba(0,0,0,0.9);
      color: white;
      padding: 10px;
      border-radius: 5px;
      font-family: monospace;
      font-size: 12px;
      z-index: 9999;
      max-width: 300px;
    `;
    
    document.body.appendChild(panel);
    
    // Update panel every second
    setInterval(() => {
      panel.innerHTML = `
        <div><strong>Performance Metrics</strong></div>
        <div>LCP: ${this.metrics.lcp?.value?.toFixed(0) || 'N/A'}ms</div>
        <div>INP: ${this.metrics.inp?.value?.toFixed(0) || 'N/A'}ms</div>
        <div>CLS: ${this.metrics.cls?.value?.toFixed(4) || 'N/A'}</div>
        <div>FCP: ${this.metrics.fcp?.value?.toFixed(0) || 'N/A'}ms</div>
        <div>TTFB: ${this.metrics.ttfb?.value?.toFixed(0) || 'N/A'}ms</div>
        <div>Long Tasks: ${this.metrics.longTasks?.length || 0}</div>
      `;
    }, 1000);
  }

  /**
   * Send metrics to analytics (customize endpoint as needed)
   */
  setupBeaconSending() {
    const sendMetrics = () => {
      // Only send if we have meaningful data
      if (Object.keys(this.metrics).length === 0) return;
      
      const data = {
        url: window.location.href,
        userAgent: navigator.userAgent,
        timestamp: Date.now(),
        metrics: this.metrics,
        shopify: {
          template: document.body.className.match(/theme-template-(\w+)/)?.[1] || 'unknown'
        }
      };
      
      // Send via beacon API (most reliable)
      if ('sendBeacon' in navigator) {
        navigator.sendBeacon('/analytics/performance', JSON.stringify(data));
      } else {
        // Fallback to fetch
        fetch('/analytics/performance', {
          method: 'POST',
          body: JSON.stringify(data),
          headers: { 'Content-Type': 'application/json' }
        }).catch(() => {}); // Silent fail
      }
      
      // Also log to console in debug mode
      if (this.debug) {
        console.log('📊 Performance metrics:', data);
      }
    };
    
    // Send on page unload
    window.addEventListener('beforeunload', sendMetrics);
    window.addEventListener('pagehide', sendMetrics);
    
    // Also send after 30 seconds for long sessions
    setTimeout(sendMetrics, 30000);
  }

  /**
   * Get current metrics snapshot
   */
  getMetrics() {
    return { ...this.metrics };
  }

  /**
   * Clean up observers
   */
  disconnect() {
    this.observers.forEach(observer => observer.disconnect());
    this.observers = [];
  }
}

// Initialize performance monitoring
if (typeof window !== 'undefined') {
  window.performanceMonitor = new PerformanceMonitor();
  
  // Add to global Eurus object if it exists
  if (window.Eurus) {
    window.Eurus.performanceMonitor = window.performanceMonitor;
  }
}
